// src/Pages/finanzas-clientes/useFinanzasClientesLogic.ts
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSidebar } from "../../hooks/useSidebar";
import { Dayjs } from "dayjs";
import { apiFetch } from "../../services/apiClient";
import { useAuth } from "../../context/AuthContext";
import { isAdminRole } from "../../utils/roles.utils";
// Re-export del tipo canónico definido en useExpensesSection.ts. Antes había
// una copia divergente que tipaba `pagado_por`/`pagado_a` como string,
// reflejando un contrato con el backend que nunca existió (son JSONB).
// Import movido arriba (junto al resto) para satisfacer la regla
// import/first de eslint-plugin-import — bloqueaba el build de Railway.
import type { ExpenseRow } from "./useExpensesSection";

export type ClienteProp = {
  id: number;
  nombrepropiedad?: string;
  id_propiedad_corto?: string;
  /**
   * Cliente dueño de la propiedad. Solo lo trae la lista del ADMINISTRADOR (`/propiedades/?limit=400`, la fila
   * completa); la del cliente es una lista blanca sin este campo. Lo usa «Descargar resumen» para decirle al servidor
   * de qué cliente es el documento (`?clienteId=`).
   */
  id_cliente?: number | null;
};

export type { ExpenseRow };

export function useFinanzasClientesLogic(targetClientId?: number) {
  const { user } = useAuth();
  const isAdmin = isAdminRole(user?.rol);
  const [loadingProps, setLoadingProps] = useState(false);
  const [loadingRows, setLoadingRows] = useState(false);

  const [propsList, setPropsList] = useState<ClienteProp[]>([]);
  const [selectedPropId, setSelectedPropId] = useState<number | null>(null);

  const [rows, setRows] = useState<ExpenseRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(20);
  const [totals, setTotals] = useState<{
    monto: number;
    currency: string;
  } | null>(null);
  const [byCategory, setByCategory] = useState<
    { categoria: string; monto: number }[]
  >([]);

  // ----- filtros -----
  const [range, setRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [cats, setCats] = useState<string[] | undefined>(undefined);
  const [rol, setRol] = useState<
    "paga" | "recibe" | "intermediario" | undefined
  >(undefined);
  const [q, setQ] = useState<string | undefined>(undefined);

  const { sidebarOpen, toggleSidebar } = useSidebar();

  // ----- NUEVO: clientes (solo para admin) -----
  // El setter quedó sin uso al eliminar fetchClients (Fase 2.5 cleanup).
  const [clientsList] = useState<
    Array<{ id: number; nombre?: string; email?: string }>
  >([]);

  const clientId = targetClientId ?? user?.id;

  // Utilidad: fijar un mes (start/end) en el rango actual
  const setMonthRange = (month: Dayjs | null) => {
    if (!month) return setRange(null);
    setRange([month.startOf("month"), month.endOf("month")]);
  };

  const getPropsByClient = useCallback(
    async (cid: number) => {
      try {
        setLoadingProps(true);
        // limit=400 (máximo del backend): un default más bajo truncaba el
        // Select de propiedad para admin.
        const path = isAdmin
          ? "/propiedades/?limit=400"
          : `/propiedades/cliente/${cid}`;
        const json = await apiFetch<{ data: ClienteProp[] }>(path);
        if (!json) return;
        const list: ClienteProp[] = json?.data || [];
        setPropsList(list);
        setSelectedPropId(list.length ? list[0].id : null);
      } finally {
        setLoadingProps(false);
      }
    },
    [isAdmin]
  );

  const fetchPropsSelf = useCallback(async () => {
    if (!user?.id) return;
    try {
      setLoadingProps(true);
      const json = await apiFetch<{ data: ClienteProp[] }>(
        `/propiedades/cliente/${user?.id}`,
      );
      if (!json) return;
      const list: ClienteProp[] = json?.data || [];
      setPropsList(list);
      if (list.length && selectedPropId == null) setSelectedPropId(list[0].id); // por defecto
    } finally {
      setLoadingProps(false);
    }
  }, [user?.id, selectedPropId]);

  const fetchRows = useCallback(
    async (p = page, s = size) => {
      if (!selectedPropId || !clientId) return;
      try {
        setLoadingRows(true);
        const params = new URLSearchParams();
        params.set("page", String(p));
        params.set("size", String(s));
        params.set("propId", String(selectedPropId));
        if (range) {
          params.set("from", range[0].toISOString());
          params.set("to", range[1].toISOString());
        }
        if (cats?.length) params.set("categoria", cats.join(","));
        if (rol) params.set("rol", rol);
        if (q) params.set("q", q);
        // Igual que antes, pero con clientId (target o self)
        const json = await apiFetch<any>(
          `/finanzas/gastos/${clientId}?${params.toString()}`,
        );
        if (!json) return;

        setRows(json.data || []);
        setTotal(json.total || 0);
        setPage(json.page || 1);
        setSize(json.size || 20);
        setTotals(json.totals || null);
        setByCategory(json.byCategory || []);
      } finally {
        setLoadingRows(false);
      }
    },
    [selectedPropId, page, size, range, cats, rol, q, clientId]
  );

  // useEffect(() => {
  //   if (isAdmin) {
  //     fetchClients();
  //   }
  // }, [isAdmin, fetchClients]);

  useEffect(() => {
    if (isAdmin) {
      getPropsByClient(user?.id ?? 0); // para admin, igual trae TODAS
    }
  }, [isAdmin, getPropsByClient, user?.id]);

  useEffect(() => {
    if (selectedPropId) fetchRows(1, size);
  }, [selectedPropId]);

  useEffect(() => {
    if (!isAdmin && user?.id) {
      fetchPropsSelf();
    }
  }, [isAdmin, user?.id, fetchPropsSelf]);

  const onChangeProp = (id?: number) => setSelectedPropId(id ?? null);
  const onPaginate = (p: number, s?: number) => fetchRows(p, s ?? size);

  const currentPropName = useMemo(() => {
    const p = propsList.find((x) => x.id === selectedPropId);
    return (
      p?.nombrepropiedad ||
      (selectedPropId ? `Propiedad #${selectedPropId}` : "")
    );
  }, [propsList, selectedPropId]);

  return {
    loadingProps,
    loadingRows,
    propsList,
    selectedPropId,
    onChangeProp,
    currentPropName,
    rows,
    total,
    page,
    size,
    onPaginate,
    totals,
    byCategory,
    range,
    setRange,
    setMonthRange,
    cats,
    setCats,
    rol,
    setRol,
    q,
    setQ,
    clientsList,
    getPropsByClient,
    fetchRows,
    toggleSidebar,
    sidebarOpen,
    isAdmin,
    user,
  };
}
